import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Checkbox, Select, type Column } from "@legal-500/legal500-design-system";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal.js";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import LoadingState from "../../../components/LoadingState/index.js";
import { TaxonomyToggle } from "../../../components/TaxonomyToggle/index.js";
import { useQuery } from "../../../hooks/useApi.js";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags.js";
import { usePermissions } from "../../../hooks/usePermissions.js";
import { usePublications } from "../../../hooks/usePublications.js";
import { useSubmissionUniques } from "../../../hooks/useSubmissionUniques.js";
import { TAXONOMY_URL_PARAM, useTaxonomyMode } from "../../../hooks/useTaxonomyMode.js";
import { useUrlParams } from "../../../hooks/useUrlParams.js";
import { ROUTES } from "../../../routes.js";
import type { LegacyPublication } from "../../../api/legacy-publications.service.js";
import type { TaxonomyMode } from "../../../api/submissions.service.js";
import {
  BACKFILL_STATUSES,
  contactMatchBackfill,
  type BackfillStatus,
  type ContactMatchBackfillRequest,
} from "../../../api/contactMatchBackfill.service.js";
import {
  publicationOptionLabel,
  taxonomyFilterParams,
  taxonomyOptionFitsMode,
  taxonomyOptionValue,
} from "../../../utils/taxonomyFilter.js";
import styles from "./ContactMatchBackfill.module.css";

/**
 * Runs submission-api's contact-match backfill (DEV-2662): re-runs the lawyer → Address
 * Book contact matcher over existing lawyer-submission entries, batch by batch, and shows
 * the per-status picture before and after.
 *
 * Same shape as the other Maintenance Scripts tools: a read-only dry run first, then a
 * real run behind a confirmation dialog. The endpoint pages with a cursor, so both loop
 * until `nextCursor` is null; a real run can be stopped between batches.
 */

/** Same flag submission-api checks; a real run is refused (409) while it's off. */
const CONTACT_MATCH_FLAG = "submission-entry-contact-match";
/** submission-api routes.ts: the backfill needs write, the stats endpoint needs read. */
const BACKFILL_PERMISSION = "staff.submission.write";
const STATS_PERMISSION = "staff.submission.read";

/** Dry runs only count, so ask for the largest page the endpoint allows. */
const DRY_RUN_PAGE_SIZE = 500;
/** A real batch matches inline (several Address Book calls per entry), so keep it small. */
const RUN_BATCH_SIZE = 50;

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  auto_matched: "Auto-matched",
  ambiguous: "Ambiguous",
  unmatched: "Unmatched",
  manual: "Manual",
  not_applicable: "Not applicable",
};

/** Column order for the stats table and the run tallies. */
const STAT_STATUSES = [
  "pending",
  "auto_matched",
  "ambiguous",
  "unmatched",
  "manual",
  "not_applicable",
] as const;
const TALLY_STATUSES = ["auto_matched", "ambiguous", "unmatched", "pending"] as const;

const ALL = "all";

type DryRunState = { status: "running"; counted: number } | { status: "done"; total: number };

type RunState = {
  status: "running" | "stopping" | "stopped" | "done" | "failed";
  processed: number;
  batches: number;
  tally: Record<string, number>;
};

function errorMessage(err: unknown, fallback: string): string {
  if ((err as { code?: string } | null)?.code === "contact_match_disabled") {
    return `Contact matching is turned off (the ${CONTACT_MATCH_FLAG} feature flag), so the backfill refused to run. Dry runs still work.`;
  }
  return err instanceof Error ? err.message : fallback;
}

type StatsRow = {
  id: string;
  label: string;
  edition: string;
  counts: Record<string, number>;
  total: number;
};

function sum(counts: Record<string, number>): number {
  return Object.values(counts).reduce((total, count) => total + count, 0);
}

export default function ContactMatchBackfill() {
  const { hasPermission } = usePermissions();
  const { hasFlag, isLoading: flagsLoading } = useFeatureFlags();
  // Both, so the stats section never 403s for a user who can only run the backfill.
  const canUse = hasPermission(BACKFILL_PERMISSION) && hasPermission(STATS_PERMISSION);
  const flagOn = hasFlag(CONTACT_MATCH_FLAG);
  const { toggleVisible, mode, taxonomy } = useTaxonomyMode();
  const { updateUrl } = useUrlParams();

  const [edition, setEdition] = useState(ALL);
  const [publicationKey, setPublicationKey] = useState(ALL);
  const [statuses, setStatuses] = useState<ReadonlySet<BackfillStatus>>(
    () => new Set(BACKFILL_STATUSES),
  );
  const [dryRun, setDryRun] = useState<DryRunState | null>(null);
  const [run, setRun] = useState<RunState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const stopRequested = useRef(false);
  /** The in-flight batch, so leaving the page can cancel it. */
  const inFlight = useRef<AbortController | null>(null);
  const mounted = useRef(true);

  // Leaving the page ends any dry run or run: no more batches go out (the in-flight one is
  // aborted), and nothing writes state into the unmounted page.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      stopRequested.current = true;
      inFlight.current?.abort();
    };
  }, []);

  /** One backfill call, abortable from the unmount cleanup. */
  const callBackfill = async (body: ContactMatchBackfillRequest) => {
    const controller = new AbortController();
    inFlight.current = controller;
    try {
      return await contactMatchBackfill.run(body, controller.signal);
    } finally {
      if (inFlight.current === controller) inFlight.current = null;
    }
  };

  // Not fetched at all without access: the page shows Access Denied instead.
  const stats = useQuery(
    () => (canUse ? contactMatchBackfill.stats(taxonomy) : Promise.resolve(null)),
    [canUse, taxonomy],
  );
  const { data: publicationsData } = usePublications(
    { edition: edition === ALL ? undefined : edition },
    { enabled: mode === "legacy" },
  );
  const { data: publicationUniques } = useSubmissionUniques(
    "publication",
    { edition: edition === ALL ? undefined : edition, taxonomy },
    { enabled: mode === "new" },
  );

  const editionOptions = useMemo(() => {
    const editions = new Set<string>();
    for (const row of stats.data?.byPublicationEdition ?? []) {
      if (row.edition) editions.add(row.edition);
    }
    return [
      { value: ALL, label: "All editions" },
      ...[...editions].sort((a, b) => b.localeCompare(a)).map(value => ({ value, label: value })),
    ];
  }, [stats.data]);

  const publicationOptions = useMemo(
    () => [
      { value: ALL, label: "All publications" },
      ...(mode === "new"
        ? (publicationUniques?.values ?? [])
            .filter(value => taxonomyOptionFitsMode("publication", value, mode))
            .map(value => ({
              value: taxonomyOptionValue("publication", value, mode),
              label: publicationOptionLabel(value),
            }))
        : (publicationsData?.publications ?? []).map((publication: LegacyPublication) => ({
            value: String(publication.id),
            label: publication.publication
              ? `${publication.publication} (${publication.edition ?? "n/a"})`
              : `Publication ${publication.id}`,
          }))),
    ],
    [publicationsData?.publications, publicationUniques?.values, mode],
  );

  const filters = (): Omit<ContactMatchBackfillRequest, "dryRun" | "limit" | "cursor"> => ({
    ...taxonomyFilterParams("publication", publicationKey, mode),
    edition: edition === ALL ? undefined : edition,
    statuses: BACKFILL_STATUSES.filter(status => statuses.has(status)),
    taxonomy,
  });

  const changeTaxonomy = (next: TaxonomyMode) => {
    setPublicationKey(ALL);
    setDryRun(null);
    updateUrl({ [TAXONOMY_URL_PARAM]: next });
  };

  const isBusy =
    dryRun?.status === "running" || run?.status === "running" || run?.status === "stopping";
  const noStatuses = statuses.size === 0;
  const runBlockedReason = !flagOn
    ? flagsLoading
      ? `Checking the ${CONTACT_MATCH_FLAG} feature flag…`
      : `Turned off by the ${CONTACT_MATCH_FLAG} feature flag. Dry runs still work.`
    : noStatuses
      ? "Pick at least one status"
      : undefined;

  const toggleStatus = (status: BackfillStatus) => {
    setStatuses(current => {
      const next = new Set(current);
      if (!next.delete(status)) next.add(status);
      return next;
    });
  };

  const runDryRun = async () => {
    setError(null);
    setRun(null);
    stopRequested.current = false;
    let counted = 0;
    let cursor: string | undefined;
    setDryRun({ status: "running", counted });
    try {
      // The dry run returns at most `limit` per call, so page through to the real total.
      do {
        const response = await callBackfill({
          ...filters(),
          dryRun: true,
          limit: DRY_RUN_PAGE_SIZE,
          cursor,
        });
        if (!mounted.current) return;
        counted += response.processed;
        cursor = response.nextCursor ?? undefined;
        setDryRun({ status: "running", counted });
      } while (cursor && !stopRequested.current);
      setDryRun({ status: "done", total: counted });
    } catch (err) {
      if (!mounted.current) return;
      setDryRun(null);
      setError(errorMessage(err, "Dry run failed"));
    }
  };

  const runBackfill = async () => {
    setIsConfirming(false);
    setError(null);
    setDryRun(null);
    stopRequested.current = false;
    const tally: Record<string, number> = {};
    let processed = 0;
    let batches = 0;
    let cursor: string | undefined;
    setRun({ status: "running", processed, batches, tally: { ...tally } });

    try {
      do {
        const response = await callBackfill({
          ...filters(),
          dryRun: false,
          limit: RUN_BATCH_SIZE,
          cursor,
        });
        if (!mounted.current) return;
        batches += 1;
        processed += response.processed;
        for (const result of response.results ?? []) {
          tally[result.status] = (tally[result.status] ?? 0) + 1;
        }
        cursor = response.nextCursor ?? undefined;
        setRun(current => ({
          status: current?.status === "stopping" ? "stopping" : "running",
          processed,
          batches,
          tally: { ...tally },
        }));
      } while (cursor && !stopRequested.current);
      setRun({
        status: cursor ? "stopped" : "done",
        processed,
        batches,
        tally: { ...tally },
      });
    } catch (err) {
      if (!mounted.current) return;
      setRun({ status: "failed", processed, batches, tally: { ...tally } });
      setError(
        `${errorMessage(err, "Backfill failed")} Stopped after ${processed.toLocaleString()} entr${
          processed === 1 ? "y" : "ies"
        } in ${batches} batch${batches === 1 ? "" : "es"}; matched entries keep their new status.`,
      );
    } finally {
      if (mounted.current) void stats.refetch();
    }
  };

  const stop = () => {
    stopRequested.current = true;
    setRun(current =>
      current?.status === "running" ? { ...current, status: "stopping" } : current,
    );
  };

  const statsRows = useMemo<StatsRow[]>(
    () =>
      (stats.data?.byPublicationEdition ?? []).map(row => ({
        id: `${row.editionId ?? row.publicationId ?? row.publicationName}:${row.edition ?? ""}`,
        label: row.publicationName,
        edition: row.edition ?? "—",
        counts: row.byStatus,
        total: sum(row.byStatus),
      })),
    [stats.data],
  );

  const statsColumns = useMemo<Column<StatsRow>[]>(
    () => [
      { header: "Publication", accessor: "label", wrap: true },
      { header: "Edition", accessor: "edition" },
      ...STAT_STATUSES.map(status => ({
        header: STATUS_LABELS[status],
        accessor: "counts" as const,
        render: (_value: unknown, row: StatsRow) => (
          <span className={styles.numeric}>{(row.counts[status] ?? 0).toLocaleString()}</span>
        ),
      })),
      {
        header: "Total",
        accessor: "total",
        render: (_value: unknown, row: StatsRow) => (
          <span className={styles.numeric}>{row.total.toLocaleString()}</span>
        ),
      },
    ],
    [],
  );

  if (!canUse) {
    return (
      <div className={styles.page}>
        <div className={styles.accessDenied} role="alert">
          <h2>Access Denied</h2>
          <p>
            You need both {BACKFILL_PERMISSION} and {STATS_PERMISSION} to use the Contact Match
            Backfill.
          </p>
        </div>
      </div>
    );
  }

  const totals = stats.data?.byStatus ?? {};

  return (
    <div className={styles.page}>
      <PageHeader
        title="Contact Match Backfill"
        subtitle="Match existing lawyer-submission entries to Address Book contacts"
      />

      <div className={styles.card}>
        <p className={styles.explainer}>
          Runs the lawyer → Address Book contact matcher over existing lawyer-recommended submission
          entries in the chosen statuses, in batches. Each entry ends up auto-matched, ambiguous
          (candidates stored for review), unmatched or still pending. Entries a researcher linked or
          marked "no contact" by hand (<strong>manual</strong>) are never changed. Once it has run,
          the CMS owner runs the{" "}
          <Link to={ROUTES.developer.editorialTransform}>editorial-transform backfill</Link> so
          already-parsed documents pick up the new links.
        </p>
      </div>

      {error ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          {error}
        </div>
      ) : null}

      <div className={styles.card}>
        <div className={styles.cardTitle}>1 · Scope</div>
        {toggleVisible && (
          <TaxonomyToggle mode={mode} onChange={changeTaxonomy} disabled={isBusy} />
        )}
        <div className={styles.filters}>
          <div className={styles.field}>
            <Select
              label="Edition"
              value={edition}
              onChange={event => {
                setEdition(event.target.value);
                setPublicationKey(ALL);
              }}
              options={editionOptions}
              size="small"
              disabled={isBusy}
            />
          </div>
          <div className={styles.field}>
            <Select
              label="Publication"
              value={publicationKey}
              onChange={event => setPublicationKey(event.target.value)}
              options={publicationOptions}
              size="small"
              disabled={isBusy}
            />
          </div>
          <fieldset className={styles.statuses}>
            <legend className={styles.fieldLabel}>Statuses</legend>
            {BACKFILL_STATUSES.map(status => (
              <Checkbox
                key={status}
                label={STATUS_LABELS[status]}
                // The design system's visible label isn't tied to the input; name it directly.
                aria-label={STATUS_LABELS[status]}
                checked={statuses.has(status)}
                onChange={() => toggleStatus(status)}
                disabled={isBusy}
              />
            ))}
          </fieldset>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.cardTitle}>2 · Dry run</div>
        <div className={styles.toolbar}>
          <Button
            variant="outlined"
            disabled={isBusy || noStatuses}
            onClick={() => void runDryRun()}
          >
            {dryRun?.status === "running" ? "Counting…" : "Dry run"}
          </Button>
          <span className={styles.rowNote}>
            Read-only: counts the entries a run would process. Works with the flag off.
          </span>
        </div>
        {dryRun ? (
          <p className={styles.progress} role="status" aria-live="polite">
            {dryRun.status === "running"
              ? `Counting… ${dryRun.counted.toLocaleString()} so far`
              : `${dryRun.total.toLocaleString()} entr${dryRun.total === 1 ? "y" : "ies"} would be processed`}
          </p>
        ) : null}
      </div>

      <div className={styles.card}>
        <div className={styles.cardTitle}>3 · Run</div>
        <div className={styles.toolbar}>
          <Button
            variant="contained"
            disabled={isBusy || runBlockedReason !== undefined}
            title={runBlockedReason}
            onClick={() => setIsConfirming(true)}
          >
            Run backfill
          </Button>
          {run?.status === "running" || run?.status === "stopping" ? (
            <Button variant="outlined" disabled={run.status === "stopping"} onClick={stop}>
              {run.status === "stopping" ? "Stopping after this batch…" : "Stop"}
            </Button>
          ) : null}
          {runBlockedReason ? <span className={styles.rowNote}>{runBlockedReason}</span> : null}
        </div>

        {run ? (
          <div className={styles.stack}>
            <p className={styles.progress} role="status" aria-live="polite">
              {`${run.processed.toLocaleString()} entr${run.processed === 1 ? "y" : "ies"} processed in ${run.batches} batch${run.batches === 1 ? "" : "es"}`}
              {run.status === "done" ? " — done" : null}
              {run.status === "stopped" ? " — stopped" : null}
            </p>
            <div className={styles.tiles}>
              {TALLY_STATUSES.map(status => (
                <div
                  key={status}
                  className={`${styles.tile} ${
                    status === "auto_matched" ? styles.tileOk : styles.tileNeutral
                  }`}
                >
                  <div className={styles.tileNumber}>
                    {(run.tally[status] ?? 0).toLocaleString()}
                  </div>
                  <div className={styles.tileLabel}>{STATUS_LABELS[status]}</div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <section className={styles.card} aria-labelledby="contact-match-stats-title">
        <div className={styles.toolbar}>
          <div id="contact-match-stats-title" className={styles.cardTitle}>
            Status overview
          </div>
          <span className={styles.spacer} />
          <Button
            variant="outlined"
            size="small"
            disabled={stats.isRefetching}
            onClick={() => void stats.refetch()}
          >
            {stats.isRefetching ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
        {stats.loading ? <LoadingState message="Loading contact-match stats..." /> : null}
        {stats.error ? (
          <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
            Couldn't load the stats: {stats.error.message}
          </div>
        ) : null}
        {stats.data ? (
          <div className={styles.stack}>
            <div className={styles.tiles} aria-label="Entries by status">
              {STAT_STATUSES.map(status => (
                <div key={status} className={`${styles.tile} ${styles.tileNeutral}`}>
                  <div className={styles.tileNumber}>{(totals[status] ?? 0).toLocaleString()}</div>
                  <div className={styles.tileLabel}>{STATUS_LABELS[status]}</div>
                </div>
              ))}
            </div>
            <DataTable
              data={statsRows}
              columns={statsColumns}
              emptyMessage="No lawyer-submission entries yet."
            />
          </div>
        ) : null}
      </section>

      {isConfirming ? (
        <ConfirmationModal
          title="Run the contact match backfill"
          message={`Match ${
            dryRun?.status === "done" ? `${dryRun.total.toLocaleString()} ` : ""
          }entries in the selected scope to Address Book contacts, ${RUN_BATCH_SIZE} per batch.`}
          warningMessage={
            "Matched entries change status straight away (auto-matched, ambiguous or unmatched); " +
            "there is no bulk undo. Manually linked entries are never touched."
          }
          confirmButtonText="Run backfill"
          backdropClick={false}
          onClose={() => setIsConfirming(false)}
          onConfirm={runBackfill}
        />
      ) : null}
    </div>
  );
}
